import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Modal, Spin } from 'antd';
import { ehrByName } from '../../constants';
import useInsuranceBillingStatus from 'hooks/useInsuranceBillingStatus';
import EhrBadge from '../EhrBadge';
import s from './s.module.less';

const ChevronIcon = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="m6 9 6 6 6-6" />
    </svg>
);

interface Props {
    open: boolean;
    // 已连接的 EHR 列表（每个 EHR 一张确认卡）
    ehrs: string[];
    organizationName?: string;
    activating?: boolean;
    onBack: () => void;
    onActivate: () => void;
}

// 提交后的 Grant-access 确认弹窗（原型 showGrantModal）：
// 用户逐个确认已在每个 EHR 里给 Klarity 代理账号分配 Biller 角色，全勾后才能 Activate
export default function GrantAccessModal({ open, ehrs, activating, onBack, onActivate }: Props) {
    const [checks, setChecks] = useState<Record<string, boolean>>({});
    const [helpOpen, setHelpOpen] = useState<Record<string, boolean>>({});
    const [billingStatus, statusLoading, refetchBillingStatus] = useInsuranceBillingStatus();
    const email = billingStatus?.agentEmail || '';
    // 兜底重拉期间的占位：email 未就绪时显示 loading，拉到后自动替换
    const emailNode = email || (statusLoading ? <Spin size="small" /> : '');
    const allChecked = ehrs.length > 0 && ehrs.every((n) => checks[n]);

    useEffect(() => {
        if (open) {
            setChecks({});
            setHelpOpen({});
            // 全局缓存可能还是 get-started 时 agentEmail 未生成的旧值：打开时缺失则重拉
            if (!billingStatus?.agentEmail) refetchBillingStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <Modal open={open} onCancel={onBack} footer={null} width={560} destroyOnClose>
            <div className={s.head}>
                <h3>Grant Klarity billing access</h3>
                <p>
                    Klarity needs biller permissions in your EHR to submit claims on your behalf. Make sure the Biller
                    role is assigned.
                </p>
            </div>
            <div className={s.cards}>
                {ehrs.map((name) => {
                    const meta = ehrByName(name);
                    return (
                        <div key={name} className={s.card}>
                            <div className={s.cardHead}>
                                <EhrBadge ehr={meta} />
                                <span className={s.ehrName}>{name}</span>
                            </div>
                            <div className={s.cardBody}>
                                <p className={s.instr}>
                                    In {name}, assign the Biller role to <strong>{emailNode}</strong>
                                </p>
                                <div className={s.emailRow}>
                                    <span className={s.emailLabel}>Agent email:</span>{' '}
                                    <span className={s.email}>{emailNode}</span>
                                </div>
                                <Button
                                    type="link"
                                    className={s.helpBtn}
                                    onClick={() => setHelpOpen((prev) => ({ ...prev, [name]: !prev[name] }))}
                                >
                                    <ChevronIcon />
                                    Need help?
                                </Button>
                                {helpOpen[name] && (
                                    <div className={s.hint}>
                                        In {name}, open Settings, go to Team or Users, find the agent account, and set
                                        its role to Biller. Then check the box below.
                                    </div>
                                )}
                                <Checkbox
                                    checked={!!checks[name]}
                                    onChange={(e) => setChecks((prev) => ({ ...prev, [name]: e.target.checked }))}
                                >
                                    Klarity has biller access in {name}
                                </Checkbox>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className={s.foot}>
                <Button onClick={onBack}>Back</Button>
                <Button type="primary" disabled={!allChecked} loading={activating} onClick={onActivate}>
                    Activate insurance billing
                </Button>
            </div>
        </Modal>
    );
}
